import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

const GOLD = '#d4af37';
const QR_DEFAULT = 'https://appguardiansfit.com/granada';
const SLIDE_MS = 9000;
const NARRATIVE_MS = 13000; // más tiempo para leer párrafos largos

const SLIDES = [
  // ============ Bloque narrativo / manifiesto ============
  {
    type: 'narrative',
    kicker: 'QUÉ ES GUARDIANSFIT',
    body: 'GUARDIANSFIT es un sistema de entrenamiento y acompañamiento que se adapta a cada persona para ayudarla a desarrollar, recuperar o conservar las capacidades que necesita para vivir mejor.',
    duration: NARRATIVE_MS,
  },
  {
    type: 'narrative',
    kicker: 'EL PROBLEMA',
    body: 'Muchas personas entrenan sin una estructura clara, repiten siempre lo mismo, no saben si están progresando y muchas veces terminan abandonando o dependiendo completamente de un entrenador.',
    duration: NARRATIVE_MS,
  },
  {
    type: 'narrative',
    kicker: 'NUESTRO ENFOQUE',
    body: 'Aportamos estructura, adaptación y acompañamiento humano mediante una forma de trabajar honesta y real, basada en experiencia con personas que llevan incluso más de tres años con nosotros.',
    duration: NARRATIVE_MS,
  },
  {
    type: 'narrative',
    kicker: 'NUESTRO OBJETIVO',
    body: 'Ordenar, medir y sostener el progreso de cada usuario mediante programas por fases, seguimiento humano y una herramienta digital escalable que permita llegar a más personas y trabajar también con otros entrenadores.',
    duration: NARRATIVE_MS,
  },
  // ============ Bloque de producto (existente) ============
  {
    type: 'intro',
    kicker: 'GUARDIANSFIT',
    title: 'Tu entrenamiento,\nsiempre ordenado.',
    sub: 'Plan, seguimiento y revisión desde la app GUARDIANSFIT.',
  },
  {
    type: 'screen',
    img: '/demo/slide-session.png',
    kicker: 'TU SESIÓN DE HOY',
    title: 'Cada día sabes exactamente qué toca.',
    sub: 'Series, repeticiones, descansos y notas claras. Sin improvisar.',
  },
  {
    type: 'screen',
    img: '/demo/slide-portal.png',
    kicker: 'PROGRESIÓN POR FASES',
    title: 'El plan progresa por fases. Tú entrenas con orden.',
    sub: 'Cada fase se ajusta al objetivo, al nivel y a la evolución del usuario.',
  },
  {
    type: 'screen',
    img: '/demo/slide-chat.png',
    kicker: 'SEGUIMIENTO HUMANO',
    title: 'Seguimiento humano. No solo automatización.',
    sub: 'Tus dudas y revisiones pasan por un entrenador real.',
  },
  {
    type: 'pricing',
    kicker: 'PRECIOS CLAROS',
    title: 'Sin permanencia. Sin letra pequeña.',
    tiers: [
      { name: 'Free', price: '0€', detail: 'Empieza sin tarjeta' },
      { name: 'Pro Mensual', price: '9,99€/mes', detail: '14 días de prueba gratis', featured: true },
      { name: 'Pro Anual', price: '79€/año', detail: 'Ahorra frente al plan mensual' },
    ],
    sub: 'Cancela cuando quieras, en un clic.',
  },
  {
    type: 'cta',
    kicker: 'EMPIEZA HOY',
    title: 'Escanea. Responde 2 minutos.\nEmpieza esta semana.',
  },
];

const Kicker = ({ children }) => (
  <p className="tracking-[0.35em] text-sm md:text-base font-semibold mb-6" style={{ color: GOLD }}>
    {children}
  </p>
);

export default function DemoLoopPage() {
  const [searchParams] = useSearchParams();
  const qrUrl = searchParams.get('qr') || QR_DEFAULT;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    const current = SLIDES[idx];
    const ms = current?.duration || SLIDE_MS;
    const t = setTimeout(() => setIdx((i) => (i + 1) % SLIDES.length), ms);
    return () => clearTimeout(t);
  }, [idx]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight') setIdx((i) => (i + 1) % SLIDES.length);
      if (e.key === 'ArrowLeft') setIdx((i) => (i - 1 + SLIDES.length) % SLIDES.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const slide = SLIDES[idx];
  const animKey = useMemo(() => `slide-${idx}`, [idx]);

  return (
    <div className="fixed inset-0 bg-black text-white overflow-hidden select-none" data-testid="demo-loop-page" style={{ cursor: 'none' }}>
      <style>{`
        @keyframes dlFadeUp { from { opacity: 0; transform: translateY(24px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes dlFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes dlBar { from { width: 0%; } to { width: 100%; } }
        .dl-anim-1 { animation: dlFadeUp .7s ease-out both; }
        .dl-anim-2 { animation: dlFadeUp .7s .15s ease-out both; }
        .dl-anim-3 { animation: dlFadeIn .9s .3s ease-out both; }
      `}</style>

      {/* Marca de agua honesta */}
      <div className="absolute top-6 left-8 z-20 flex items-center gap-3 dl-anim-3" key={`brand-${animKey}`}>
        <span className="text-lg md:text-xl font-bold tracking-widest" style={{ color: GOLD }}>GUARDIANSFIT</span>
        <span className="text-[10px] md:text-xs border border-zinc-700 rounded-full px-3 py-1 text-zinc-400 tracking-wider">
          PANTALLAS REALES DE LA APP · SIN MAQUETAS
        </span>
      </div>

      {/* Slide */}
      <div className="absolute inset-0 flex items-center justify-center px-10 md:px-20" key={animKey}>
        {slide.type === 'narrative' && (
          <div className="max-w-5xl text-center">
            <div className="dl-anim-1"><Kicker>{slide.kicker}</Kicker></div>
            <p
              className="dl-anim-2 mt-6 text-3xl md:text-4xl lg:text-5xl leading-[1.35] text-zinc-100 font-medium"
              style={{ letterSpacing: '-0.01em' }}
            >
              {slide.body}
            </p>
          </div>
        )}

        {slide.type === 'intro' && (
          <div className="text-center max-w-5xl">
            <div className="dl-anim-1"><Kicker>{slide.kicker}</Kicker></div>
            <h1 className="dl-anim-1 text-5xl md:text-7xl lg:text-8xl font-extrabold leading-tight whitespace-pre-line">{slide.title}</h1>
            <p className="dl-anim-2 mt-8 text-xl md:text-2xl text-zinc-400 max-w-3xl mx-auto">{slide.sub}</p>
          </div>
        )}

        {slide.type === 'screen' && (
          <div className="w-full max-w-[1700px] grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-2">
              <div className="dl-anim-1"><Kicker>{slide.kicker}</Kicker></div>
              <h2 className="dl-anim-1 text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight">{slide.title}</h2>
              <p className="dl-anim-2 mt-6 text-lg md:text-2xl text-zinc-400">{slide.sub}</p>
            </div>
            <div className="lg:col-span-3 dl-anim-3">
              <div className="rounded-xl overflow-hidden border border-zinc-800 shadow-[0_0_80px_rgba(212,175,55,0.12)]">
                <div className="h-8 bg-zinc-900 flex items-center gap-2 px-4 border-b border-zinc-800">
                  <span className="w-3 h-3 rounded-full bg-zinc-700" />
                  <span className="w-3 h-3 rounded-full bg-zinc-700" />
                  <span className="w-3 h-3 rounded-full bg-zinc-700" />
                  <span className="ml-4 text-[11px] text-zinc-500 tracking-wide">appguardiansfit.com</span>
                </div>
                <img src={slide.img} alt={slide.kicker} className="w-full block" />
              </div>
            </div>
          </div>
        )}

        {slide.type === 'pricing' && (
          <div className="text-center max-w-6xl w-full">
            <div className="dl-anim-1"><Kicker>{slide.kicker}</Kicker></div>
            <h2 className="dl-anim-1 text-4xl md:text-6xl font-extrabold">{slide.title}</h2>
            <div className="dl-anim-2 mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
              {slide.tiers.map((t) => (
                <div
                  key={t.name}
                  className="rounded-2xl border p-8 md:p-10"
                  style={{
                    borderColor: t.featured ? GOLD : '#27272a',
                    background: t.featured ? 'rgba(212,175,55,0.07)' : 'rgba(24,24,27,0.6)',
                  }}
                >
                  <p className="text-lg tracking-widest text-zinc-400 uppercase">{t.name}</p>
                  <p className="mt-3 text-4xl md:text-5xl font-extrabold" style={{ color: t.featured ? GOLD : 'white' }}>{t.price}</p>
                  <p className="mt-3 text-zinc-400 text-base md:text-lg">{t.detail}</p>
                </div>
              ))}
            </div>
            <p className="dl-anim-3 mt-10 text-xl text-zinc-400">{slide.sub}</p>
          </div>
        )}

        {slide.type === 'cta' && (
          <div className="text-center">
            <div className="dl-anim-1"><Kicker>{slide.kicker}</Kicker></div>
            <h2 className="dl-anim-1 text-4xl md:text-6xl font-extrabold whitespace-pre-line leading-tight">{slide.title}</h2>
            <div className="dl-anim-3 mt-12 inline-block bg-white p-6 rounded-2xl">
              <QRCodeSVG value={qrUrl} size={280} level="M" />
            </div>
            <p className="dl-anim-3 mt-6 text-lg text-zinc-500 tracking-wide">appguardiansfit.com</p>
          </div>
        )}
      </div>

      {/* QR persistente (excepto en slide CTA) */}
      {slide.type !== 'cta' && (
        <div className="absolute bottom-8 right-8 z-20 flex items-center gap-4">
          <p className="text-right text-sm md:text-base text-zinc-400 leading-snug">
            Escanéalo y<br />
            <span className="font-bold" style={{ color: GOLD }}>empieza hoy</span>
          </p>
          <div className="bg-white p-2.5 rounded-xl">
            <QRCodeSVG value={qrUrl} size={104} level="M" />
          </div>
        </div>
      )}

      {/* Progreso de slides */}
      <div className="absolute bottom-8 left-8 z-20 flex items-center gap-2">
        {SLIDES.map((_, i) => (
          <div key={i} className="h-1 rounded-full overflow-hidden bg-zinc-800" style={{ width: i === idx ? 64 : 20 }}>
            {i === idx && (
              <div
                className="h-full"
                style={{
                  background: GOLD,
                  animation: `dlBar ${SLIDES[idx].duration || SLIDE_MS}ms linear both`,
                }}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
